import { useMemo } from "react";

/**
 * Small SVG dandelion seed used as decorative floating element.
 * Sized via parent. Stroke uses currentColor for theming.
 */
export const DandelionSeed = ({ className = "", size = 22 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 32 48"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    {/* Pappus crown */}
    <g stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.9">
      <line x1="16" y1="2" x2="16" y2="12" />
      <line x1="9"  y1="4" x2="13" y2="12" />
      <line x1="23" y1="4" x2="19" y2="12" />
      <line x1="5"  y1="9" x2="11" y2="13" />
      <line x1="27" y1="9" x2="21" y2="13" />
      <line x1="3"  y1="14" x2="10" y2="14" />
      <line x1="29" y1="14" x2="22" y2="14" />
    </g>
    {/* Seed body */}
    <ellipse cx="16" cy="18" rx="1.4" ry="2.4" fill="currentColor" opacity="0.85" />
    {/* Floating stem */}
    <path
      d="M16 20 C 15.4 28, 16.6 36, 16 46"
      stroke="currentColor"
      strokeWidth="0.7"
      strokeLinecap="round"
      opacity="0.55"
      fill="none"
    />
  </svg>
);

/**
 * Wrapper that scatters multiple seeds across a container.
 * Each seed gets randomized animation params via CSS vars.
 */
export const FloatingSeeds = ({ count = 8, color = "#9A8F7C", className = "" }) => {
  const seeds = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const top = Math.random() * 80 + 10;
        const left = Math.random() * 90 + 5;
        const tx = (Math.random() - 0.3) * 220;
        const ty = -Math.random() * 260 - 60;
        const tr = (Math.random() - 0.5) * 60;
        const dur = 14 + Math.random() * 12;
        const delay = Math.random() * 8;
        const size = 14 + Math.random() * 14;
        return { i, top, left, tx, ty, tr, dur, delay, size };
      }),
    [count]
  );
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
      {seeds.map((s) => (
        <span
          key={s.i}
          className="seed"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            color,
            "--tx": `${s.tx}px`,
            "--ty": `${s.ty}px`,
            "--tr": `${s.tr}deg`,
            "--dur": `${s.dur}s`,
            "--delay": `${s.delay}s`,
          }}
        >
          <DandelionSeed size={s.size} />
        </span>
      ))}
    </div>
  );
};

export default DandelionSeed;
